import { Component, ElementRef, Input, OnChanges, OnInit, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
/*import { CalendarComponent } from 'ng-fullcalendar';*/
import { FullCalendarComponent as CalendarComponent } from "@fullcalendar/angular";
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";

import { AuthService } from '../../../../services/auth.service';
import { DateService } from '../../../../common/services/date.service';
import { FinancialAssuranceService } from '../../../../common/services/financial-assurance.service';
import { WorkOrderService } from '../../../../common/services/work-order.service';

import { CalendarType } from './calendar-types';
import { UserIdentity } from '../../../../models/user-identity.model';
import { WorkOrder, WorkOrderType, WorkOrderWithAssignments, WorkOrderStatus } from '../../../../common/models/work-order.model';

@Component({
    selector: 'rics-full-calendar',
    templateUrl: './full-calendar.component.html',
    styleUrls: ['./full-calendar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class FullCalendarComponent implements OnInit, OnChanges {

    public _groupColor: string = 'rgb(105, 125, 23)';
    public _myColor: string = 'rgb(9, 139, 188)';
    public _events: Array<any> = new Array<any>();
    calendarPlugins = [dayGridPlugin, listPlugin];
    calendarOptions?: CalendarOptions;
    calendarTypes: any = CalendarType;

    private _filteredEvents: Array<any> = new Array<any>();

    get filteredEvents(): Array<any> {
        return this._filteredEvents;
    }

    set filteredEvents(evt: Array<any>) {
        this._filteredEvents = evt;
        if (this.changeDetectorRef != null) {
            this.changeDetectorRef.detectChanges();
        }
    }


    groups: Array<any> = new Array<any>();
    workOrderSubscription: Subscription = new Subscription();
    insuranceSubscription: Subscription = new Subscription();
    showMyWorkOrders: boolean = true;
    get user(): UserIdentity {
        return this.authService.User;
    }

    isLoading: boolean = true;
    @Input() isActive: boolean = false;

    workOrders!: WorkOrderWithAssignments[];
    insurances!: any[];

    @Input() organizationId: string = "";
    @Input() currentView: CalendarType = CalendarType.Month;


    @ViewChild('calendar', { static: false }) public _calendar!: CalendarComponent;


    constructor(
        public authService: AuthService,
        public changeDetectorRef: ChangeDetectorRef,
        public dateService: DateService,
        public _element: ElementRef,
        public financialAssuranceService: FinancialAssuranceService,
        public workOrderService: WorkOrderService,
        public _renderer: Renderer2,
        public _router: Router
    ) { }

    ngOnInit() {

        this.bindRouteChangeEvent();
        const sharedOptions: CalendarOptions = {
            editable: false,
            weekends: true,
            headerToolbar: {
                start: 'prev',
                center: 'title today',
                end: 'next'
            },
            plugins: this.calendarPlugins,
            eventClick: (arg) => this.eventClick(arg)
        };

        if (this.currentView === CalendarType.ListWeek) {
            this.calendarOptions = { ...sharedOptions, initialView: this.currentView, height: '100%' } as CalendarOptions;
        } else {
            this.calendarOptions = { ...sharedOptions, initialView: this.currentView, contentHeight: 'auto', height: '100%' } as CalendarOptions;
        }
        

        const types = new Array<WorkOrderType>();
        types.push(WorkOrderType.AdHoc);
        types.push(WorkOrderType.AdHocReview);
        types.push(WorkOrderType.Review);
        types.push(WorkOrderType.ScanDocuments);
        types.push(WorkOrderType.ReviewScannedDocuments);
        types.push(WorkOrderType.LegalReviewScannedDocuments);


        if (!this.user && !this.organizationId.length) {
            this.organizationId = this.user.organizationId;
        }

        if (this.user) {
            //Only fetch pending work items, we only care about ones they can actaully do something with for now.
            this.workOrderSubscription.add(this.workOrderService.getUserWorkOrders(this.user.id, types, [WorkOrderStatus.Pending])
                .subscribe({
                    next: userWorkOrders => {
                        this.workOrders = userWorkOrders;
                        this.createEventsFromWorkOrderAssignments();
                    }, error: (error) => {
                            console.error(error);
                            this.isLoading = false;            
                    } 
                }
            ));
            if (this.user.organizationId) {
                this.insuranceSubscription.add(this.financialAssuranceService.getInsurances(this.user.organizationId)
                   .subscribe({
                        next: data => {
                            this.insurances = data;
                            this.createEventsFromInsurances();
                    }, error: (error) => {
                       console.error(error);
                       this.isLoading = false;
                
                   }
                }));
            }
        } else if (this.organizationId.length) {
            types.push(WorkOrderType.Permit);
            this.workOrderSubscription.add(this.workOrderService.getOrganizationWorkOrders(this.organizationId, types)
                .subscribe(orgWorkOrders => {
                this.workOrders = orgWorkOrders;
                this.createEventsFromWorkOrderAssignments();
            }, (error) => {
                    console.error(error);
                    this.isLoading = false;
            }));
        } else {
            this.isLoading = false;
        }
    }

    ngOnDestroy() {
        this.workOrderSubscription.unsubscribe();
        this.insuranceSubscription.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['isActive']?.currentValue === true && this._calendar) {
            this._calendar.getApi().updateSize();
        }
    }

    private bindRouteChangeEvent(): void {
        this._router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.workOrderSubscription.unsubscribe();
            this.insuranceSubscription.unsubscribe();
        });
    }

    public createEventsFromWorkOrderAssignments(): void {
        let events: any[] = [];
        this.workOrders.forEach((workOrder: WorkOrderWithAssignments) => {
            if (workOrder.type === WorkOrderType.AdHoc) {
                if (!workOrder.applicationNumber) {
                    workOrder.applicationNumber = "View Details";
                }
                if (!workOrder.targetUrl) {
                    workOrder.targetUrl = `/common/work-order/${workOrder.id}`
                }
            }
            let evt = {
                id: workOrder.id,
                title: workOrder.applicationNumber,
                start: workOrder.dueDate,
                allDay: true,
                color: this.findEventColor(workOrder),
                creatorId: workOrder.creatorId,
                assignedUserId: workOrder.assignedUserId,
                assignedGroupId: workOrder.assignedGroupId,
                assignedUser: workOrder.assignee,
                assignedGroup: workOrder.assignedGroup ? workOrder.assignedGroup.displayName : "",
                type: workOrder.type,
                targetUrl: workOrder.targetUrl,
                workOrderTitle: workOrder.title
            }
            events.push(evt);
        });

        let groups = this.distinct(this.workOrders.map(workOrder => workOrder.assignedGroup).filter(workOrder => workOrder !== null && workOrder !== undefined), 'id');
        this.groups = groups;
        this.groups.forEach(x => x.selected = true);

        this._events = this._events.concat(events);
        this.filteredEvents = this._events;
         if (!this.user || !this.user.organizationId || this.insurances) {
            this.isLoading = false;
        }

    }

    private createEventsFromInsurances(): void {
        let events: any[] = [];
        this.insurances.forEach((insurance) => {
            insurance.policies.forEach((policy) => {
                events.push({
                    id: policy.policyNumber,
                    title: policy.insurer,
                    start: this.dateService.convertUtcStringToYYYYMMDD(policy.expirationDate),
                    allDay: true,
                    color: this._myColor,
                    assignedUserId: this.user.id,
                    assignedUser: insurance.agent,
                    assignedGroup: 'Insurance',
                    type: policy.type,
                    workOrderTitle: insurance.agency
                });
            });
        });
        this._events = this._events.concat(events);
        this.filteredEvents = this._events;
        if (this.workOrders) {
            this.isLoading = false;
        }
    }

    eventClick(event: any): void {
        const targetUrl = event.event?.extendedProps?.targetUrl;
        if (targetUrl?.length > 0) {
            this._router.navigate([targetUrl]);
        }
    }

    /**
     * Render the events in the listDay and List Month Views
     * @param event
     */
    eventRender(event: any): void {

        if (event.el.children && this.currentView !== CalendarType.Month) {

            if (event.el.children.length > 2) {
                //Remove their formatting of the title i.e. our Application number so we can style it
                this._renderer.removeChild(event.el.children, event.el.children[2]);
            } 

            //Add styled version back in
            if (event.event.extendedProps.targetUrl) {
                this.addColumn(event.el, event.event.title, true, event.event.extendedProps.targetUrl);
            } else {
                this.addColumn(event.el, event.event.title);
            }


            if (event.view.viewSpec.type === 'listDay' || event.view.viewSpec.type === 'listMonth' || event.view.viewSpec.type === 'list') {
                let user = event.event.extendedProps.assignedUser;
                let assignedGp = event.event.extendedProps.assignedGroup;

                this.addColumn(event.el, event.event.extendedProps.workOrderTitle);
                this.addColumn(event.el, this.getWorkOrderType(event.event.extendedProps.type));
                this.addColumn(event.el, user !== null && user !== undefined ? user.displayName : assignedGp);
            }

        }

    }

    /*
     * Filter the Events based on the selected groups
     */
    filterEvents(): void {
        let filteredEvents = this._events;
        if (!this.showMyWorkOrders) {
            filteredEvents = filteredEvents.filter((event) => {
                if (event.assignedUserId !== this.user.id || event.assignedGroup === 'Insurance') {
                    return true;
                }
                return false;
            });
        }

        this.groups.filter(group => !group.selected).forEach(group => {
            filteredEvents = filteredEvents.filter(event => event.assignedGroupId !== group.id);
        });

        this.filteredEvents = filteredEvents;
    }

    setHeaders(): void {

        let headers = this._element.nativeElement.querySelectorAll('.fc-list-heading');

        for (let i = 0; i < headers.length; i++) {
            let header = headers[i];

            if (header === null || header.children[0].colSpan === 1)
                continue;

            if (this.currentView === CalendarType.ListMonth && header.children[0].children[1])
                this._renderer.removeChild(header.children[0], header.children[0].children[1]);

            if (this.currentView !== CalendarType.ListWeek) {
                header.children[0].children[0].innerText = `${header.children[0].children[0].innerText}`;
                this._renderer.setAttribute(header.children[0], 'colspan', '1');
                this._renderer.insertBefore(header, document.createElement('td'), header.children[0]);
                this.addColumn(header, 'Title');
                this.addColumn(header, 'Type');
                this.addColumn(header, 'Assigned To');
            }

        }
    }

    toggleView(): void {
        if (this.currentView === CalendarType.Month)
            this.currentView = CalendarType.ListMonth;
        else if (this.currentView === CalendarType.ListMonth)
            this.currentView = CalendarType.Month;
        else if (this.currentView === CalendarType.ListDay)
            this.currentView = CalendarType.Month;

        this._calendar.getApi().changeView(this.currentView.toString());
        this.setHeaders();
    }

    /**
     * Add a column to a given table
     * @param parentElement
     * @param value
     */
    public addColumn(parentElement: HTMLElement, value: any, makeLink: boolean = false, linkTarget: string = ""): void {
        let element = document.createElement('td');

        if (!makeLink)
            element.innerHTML = value;
        else {
            var linkElement = document.createElement('span');


            //To handle any cases where, for whatever reason, we don't have a friendly text value for the link
            //just set the actual the link instead.
            if (!value)
                value = linkTarget;
            linkElement.className = "faux-link";
            linkElement.innerText = value;
            linkElement.innerHTML = value;
            element.appendChild(linkElement);

        }

        this._renderer.appendChild(parentElement, element);
    }

    /**
     * Filter an array and return only distinct values
     * @param array
     * @param property
     */
    public distinct(array, property): Array<WorkOrder> {
        return array.filter((obj, pos, arr) => {
            return arr.map(mapObj => mapObj[property]).indexOf(obj[property]) === pos;
        });
    }

    /**
     * Sets the item color
     * @param item
     */
    public findEventColor(item: WorkOrder): string {
        if (item.assignedUserId === this.user.id)
            return this._myColor;
        else
            return this._groupColor;
    }

    public getWorkOrderType(type: WorkOrderType): string {
        switch (type) {
            case WorkOrderType.AcceptApplication:
                return 'Accept Application';
            case WorkOrderType.AdHoc:
                return 'Ad Hoc';
            case WorkOrderType.AdHocReview:
                return 'Ad Hoc Review';
            case WorkOrderType.CertifiedMail:
                return 'Certified Mail';
            case WorkOrderType.Permit:
                return 'Permit Application';
            case WorkOrderType.Resubmit:
                return 'Resubmit Application';
            case WorkOrderType.Review:
                return 'Review';
            case WorkOrderType.ReviewExpedite:
                return 'Review Expedite Request';
            case WorkOrderType.ScanDocuments:
                return 'Scan Documents';
            case WorkOrderType.StandardMail:
                return 'Send Standard Mail';
            case WorkOrderType.WellTransfer:
                return 'Well Transfer Application';
            default:
                return 'Unknown';
        }
    }

    viewFullCalendar(): void {
        this._router.navigate(['/dashboard/view/nR2HHRPnR2IOLvH32']);
    }

}
